import { GetOptionsProps, InlinePopupProps, patternToRegExp } from "csi/middleware/inline/InlinePopup";
import { DelayedRequest, DelayedRequestState } from "csi/store/DelayedRequest";
import { StoreNames } from "csi/store/Names";
import { doesStoreUseSlimCard, getStoreSuggestedGroups, shouldGroupStoreItems } from "csi/store/common";
import { useEffectAsync } from "designSystem/hooks";
import { Button, SingleSelect } from "designSystem/index";
import { useLocale } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { NONE_COMPONENT_ID } from "designSystem/utilities/component-item-functions";
import { stopMuiEvent } from "designSystem/utilities/prevent-default-behavior";
import React, { FunctionComponent, KeyboardEventHandler, SyntheticEvent, useRef, useState } from "react";
import DropdownPlaceholder from "./DropdownPlaceholder";

const InlineSingleSelect: FunctionComponent<InlinePopupProps> = (props) => {
	const { allowPinyin, customRenderOption, dropdownOpen, getOptions, onCancel, onChange, onDialogClose, onLastInputChange, placeholderOptions, required } = props;
	const [selectOptions, setSelectOptions] = useState<GetOptionsProps>(placeholderOptions);
	const { delaySearch, store } = selectOptions;
	const [delayedRequestState, setDelayedRequestState] = useState<DelayedRequestState>(DelayedRequestState.WAITING);
	const loc = useLocale();

	const [requestSent, setRequestSent] = useState<boolean>(false);

	const inputTextRef = useRef("");
	let inputText = inputTextRef.current;

	let newValue: ComponentItem;
	let highlightedValue: ComponentItem;
	let highlightedByKeyboard = false;

	let sourceStore: StoreNames;

	if (store) {
		if ("bDelayedRequest" in store) {
			sourceStore = store.sourceStore;
		} else {
			sourceStore = store;
		}
	}
	const shouldGroupItems = shouldGroupStoreItems(sourceStore);
	const suggestedGroups = getStoreSuggestedGroups(sourceStore);
	const slimCard = doesStoreUseSlimCard(sourceStore);
	const emptyValue = sourceStore?.getEmptyValue?.() || "";

	const onOpenCallback = async () => {
		setRequestSent(true);
	};

	const removeEmptyItemIfRequired = (options) => {
		if (required && options[0]?.value == emptyValue) {
			options.shift();
		}
	};

	const addDelayedSearchEmptyItem = (options) => {
		if (delaySearch && sourceStore &&!required) {
			if (options[0]?.value != emptyValue) {
				const emptyItem = {
					id: NONE_COMPONENT_ID,
					value: emptyValue,
					name: loc.resource("SelectNone"),
				};
				options.splice(0, 0, emptyItem);
			}
		}
	};
	const onDelayedSearchStateChange = (state) => {
		if (state === DelayedRequestState.RESULTS_REFRESHED) {
			let options = (store as DelayedRequest).getResultsReact();
			const { value } = selectOptions;
			// options must always include value to avoid mui autocomplete exception
			if (value) {
				if (options.length == 0) {
					options = [value];
				} else if (options.filter((item)=>item.id == value.id).length == 0) {
					// if first option is empty line - insert selected value after empty line;
					const insertPos = options[0].name == "" ? 1 : 0;
					options.splice(insertPos, 0, value);
				}
			}
			removeEmptyItemIfRequired(options);
			addDelayedSearchEmptyItem(options);
			setSelectOptions({ ...selectOptions, options: options, open: true });
		}
		setDelayedRequestState(state);
	};

	useEffectAsync(async () => {
		if (delaySearch) {
			const delayedStore: any = store;
			delayedStore.onChangeState = onDelayedSearchStateChange;
		}
		if (requestSent || dropdownOpen) {
			const optionsProps = delaySearch ? selectOptions : await getOptions(store);
			const { options } = optionsProps;
			removeEmptyItemIfRequired(options);
			addDelayedSearchEmptyItem(options);
			setSelectOptions({ ...optionsProps, options, open: true });
		}
	}, [requestSent]);

	const onChangeValue = (ev: SyntheticEvent, val: any): void => {
		newValue = val;
		onChange(ev, val?.value);
	};

	// process TAB and Enter
	const onKeyDown: KeyboardEventHandler = (ev) => {
		const { key, target } = ev;
		const { open } = selectOptions;

		// if user hit "Enter" with delayed search and not empty filter - do not navigate away, allow delayed request query instead
		if ((key === "Enter") && delaySearch && open && inputText && (target as HTMLElement).tagName === "INPUT") {
			stopMuiEvent(ev);
			onInputChange(null, null, "input");
		} else if ((key === "Enter") || (key === "Tab")) {
			// stop event to prevent focus shit, but call onChangeValue onKeyUp (below);
			stopMuiEvent(ev);
		} else if (key === "Escape") {
			stopMuiEvent(ev);
			onCancel();
		}
	};

	// TAB and ENTER move editing to the next table cell, and if TAB or ENTER click processed on key down, then KeyUp might be processed by next editing component 
	// so, disable onKeyDown TAN and ENTER processing and process keyUp for TAB here
	const onKeyUp: KeyboardEventHandler = (ev) => {
		const { key, target } = ev;
		const { open } = selectOptions;

		// if user hit "Enter" with delayed search and not empty filter - do not navigate away, allow delayed request query instead
		if ((key === "Enter") && delaySearch && open && inputText && (target as HTMLElement).tagName === "INPUT") {
			return;
		}
		if ((key === "Enter") || (key === "Tab")) {
			setSelectOptions({ options: [], value: null, store: null, open: false });

			if ((key === "Tab") && highlightedValue) {
				// select highlighted value on tab if filter is not empty and highlighted value match entered filter
				if (inputText) {
					const regex = new RegExp(inputText, "i");
					if (highlightedValue.name.match(regex)) {
						onChangeValue(ev, highlightedValue);
						return;
					}
				} 
				// select highlighted value on tab selection was made via keyboard (arrow up/down)
				if (highlightedByKeyboard) {
					onChangeValue(ev, highlightedValue);
					return;
				}
			}
			// select highlighted value on enter or use new value if no highlight
			onChangeValue(ev, ((key === "Enter") && highlightedValue) ? highlightedValue : newValue);
		}
	};


	const onInputChange = (event: React.SyntheticEvent, value: string, reason: string) => {
		// update reactLastInput on controller for user input
		// set it to "" for all other change reasons (example reason=="reset")
		onLastInputChange(reason === "input" ? value : "");

		if (delaySearch && (reason === "input")) {
			if (value === null) {
				// DelayedSearch and Enter clicked
				(store as DelayedRequest).skipDelayOnNextQuery = true;
			} else {
				inputText = inputTextRef.current = value;
			}
			const storeOptions = { ignoreCase: true };
			const qs = "*" + inputText + "*";
			const rs = patternToRegExp(qs);
			rs.toString = () => qs;
			const query = { Name: rs };
			getOptions(store, query, storeOptions);
		} else {
			inputText = inputTextRef.current = value;
		}
	};

	const onHighlightChange = (event: React.SyntheticEvent, option: ComponentItem, reason: string): void => {
		if (reason !== "mouse") {
			highlightedByKeyboard = reason === "keyboard";
			highlightedValue = option;
		}
	};

	const actions = selectOptions.selectActions?.map((item, idx) => (
		<Button
			design="link"
			fullWidth
			hideOverflow
			key={idx}
			onClick={() => item.onAction(newValue?.value)}
			res={item.res}
		>{item.label}</Button>
	));

	return (
		<SingleSelect
			DropdownPlaceholder={delaySearch && DropdownPlaceholder({ state: delayedRequestState, actions, onDialogClose: onCancel })}
			allowPinyin={allowPinyin}
			autoHighlight
			clearOnBlur={false}
			customRenderOption={customRenderOption}
			delayedRequestHasResults={delaySearch && (delayedRequestState === DelayedRequestState.RESULTS)}
			disableClearable
			grouped={shouldGroupItems}
			id="csi-automation-inline-singleselect"
			inline
			inputAutoFocus
			inputSelectOnFocus
			inputValue={inputText}
			noInputToggle
			onCancel={onCancel}
			onChange={onChangeValue}
			onHighlightChange={onHighlightChange}
			onInputChange={onInputChange}
			onKeyDown={onKeyDown}
			onKeyUp={onKeyUp}
			onOpenCallback={onOpenCallback}
			open={selectOptions.open || false}
			options={selectOptions.options}
			placeHolderRes="Select"
			required={required}
			selectActions={actions}
			slimCard={slimCard}
			suggestedGroups={suggestedGroups}
			value={selectOptions.value}
		/>
	);
};

export default InlineSingleSelect;
